import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, X, ShieldAlert, Save } from 'lucide-react';
import { getPermissionCatalog, getUserPermissions, setUserPermissions, clearUserPermissions } from '../services/api';
import { toast } from '../hooks/use-toast';

// Renders a grid of (resource × action) checkboxes.
// `user` must be { user_id, name, email, role }.
const PermissionsDialog = ({ user, onClose }) => {
  const [catalog, setCatalog] = useState({ resources: [], actions: [] });
  const [permissions, setPermissions] = useState({}); // { [resource]: string[] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, current] = await Promise.all([getPermissionCatalog(), getUserPermissions(user.user_id)]);
      setCatalog(cat);
      setPermissions(current?.permissions || {});
    } catch (err) {
      toast({ title: 'Failed to load permissions', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [user.user_id]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const g = {};
    (catalog.resources || []).forEach((r) => {
      (g[r.group] = g[r.group] || []).push(r);
    });
    return g;
  }, [catalog.resources]);

  const toggle = (resKey, action) => {
    setPermissions((prev) => {
      const list = new Set(prev[resKey] || []);
      if (list.has(action)) list.delete(action);
      else list.add(action);
      return { ...prev, [resKey]: Array.from(list) };
    });
  };
  const toggleRow = (resKey, allChecked) => {
    setPermissions((prev) => ({ ...prev, [resKey]: allChecked ? [] : [...catalog.actions] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await setUserPermissions(user.user_id, permissions);
      toast({ title: 'Permissions updated', description: `Custom access saved for ${user.name}` });
      onClose(true);
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  const clearAll = async () => {
    if (!window.confirm(`Remove ALL custom permissions for ${user.name}? They'll be restricted to their role's defaults.`)) return;
    setSaving(true);
    try {
      await clearUserPermissions(user.user_id);
      toast({ title: 'Cleared', description: 'All custom permissions removed' });
      onClose(true);
    } catch { toast({ title: 'Clear failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const isSuperUser = user.role === 'super_admin' || user.role === 'admin';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => onClose(false)} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">Emergency access</div>
            <div className="text-lg font-semibold text-[#0b1a2e]">Custom permissions · {user.name}</div>
            <div className="text-xs text-[#0b1a2e]/60 mt-0.5">{user.email} · role: {user.role}</div>
          </div>
          <button onClick={() => onClose(false)} className="p-2 hover:bg-black/5 rounded-lg" data-testid="permissions-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {isSuperUser ? (
          <div className="p-8 text-center">
            <ShieldAlert className="w-8 h-8 text-[#0f5c3d] mx-auto mb-3" />
            <div className="text-sm text-[#0b1a2e]">This user is already <strong>{user.role}</strong> and has full access.</div>
            <div className="text-xs text-[#0b1a2e]/60 mt-1">Custom permissions apply only to Staff and Content Publishers.</div>
          </div>
        ) : loading ? (
          <div className="p-10 text-center text-[#0b1a2e]/60">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className="px-6 py-3 bg-[#f7fbf8] border-b border-black/5 text-xs text-[#0b1a2e]/70">
              Tick the boxes to grant extra access that goes <strong>beyond</strong> this user's role. Their role always
              stays intact — this only <em>adds</em> permissions.
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {Object.entries(groups).map(([groupName, resources]) => (
                <div key={groupName}>
                  <div className="text-[10px] uppercase tracking-widest text-[#0f5c3d] font-semibold mb-2">{groupName}</div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/50">
                        <th className="text-left py-1.5 font-medium">Resource</th>
                        {catalog.actions.map((a) => (
                          <th key={a} className="text-center py-1.5 font-medium w-16">{a}</th>
                        ))}
                        <th className="text-center py-1.5 font-medium w-20">All</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.map((r) => {
                        const granted = new Set(permissions[r.key] || []);
                        const allChecked = catalog.actions.every((a) => granted.has(a));
                        return (
                          <tr key={r.key} className="border-t border-black/5">
                            <td className="py-2 text-[#0b1a2e]">{r.label}</td>
                            {catalog.actions.map((a) => (
                              <td key={a} className="py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={granted.has(a)}
                                  onChange={() => toggle(r.key, a)}
                                  data-testid={`perm-${r.key}-${a}`}
                                  className="accent-[#0f5c3d] w-4 h-4"
                                />
                              </td>
                            ))}
                            <td className="py-2 text-center">
                              <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={() => toggleRow(r.key, allChecked)}
                                className="accent-[#0b1a2e] w-4 h-4"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex justify-between items-center gap-2">
              <button onClick={clearAll} disabled={saving} data-testid="permissions-clear-btn" className="text-xs text-red-600 hover:underline">
                Clear all custom permissions
              </button>
              <div className="flex gap-2">
                <button onClick={() => onClose(false)} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="permissions-save-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save permissions
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PermissionsDialog;
